import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getDupLang } from '../../translations/dup_lang';
import { DuplicateMatch } from '../../lib/services/duplicateService';
import { Customer } from '../../types/customer';
import Modal from '../common/Modal';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface DuplicateMergeModalProps {
  match: DuplicateMatch;
  onClose: () => void;
  onMerge: (primaryId: string, secondaryId: string, mergedData: Partial<Customer>) => Promise<void>;
}

const mergeableFields: { key: keyof Customer; label: string }[] = [
  { key: 'companyName', label: 'Cégnév' },
  { key: 'firstName', label: 'Keresztnév' },
  { key: 'lastName', label: 'Vezetéknév' },
  { key: 'email', label: 'E-mail' },
  { key: 'phone', label: 'Telefonszám' },
  { key: 'taxNumber', label: 'Adószám' },
  { key: 'notes', label: 'Megjegyzések' },
];

const DuplicateMergeModal: React.FC<DuplicateMergeModalProps> = ({ match, onClose, onMerge }) => {
  const { language } = useLanguage();
  const tr = getDupLang(language);
  const [primaryId, setPrimaryId] = useState<string>(match.customers[0].id);
  const [selectedFields, setSelectedFields] = useState<Record<string, 'primary' | 'secondary'>>({});
  const [merging, setMerging] = useState(false);

  const primary = match.customers.find(c => c.id === primaryId) || match.customers[0];
  const secondary = match.customers.find(c => c.id !== primaryId) || match.customers[1];

  const getCustomerName = (c: Customer) => {
    if (c.companyName) return c.companyName;
    if (c.firstName || c.lastName) return `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return c.email || c.id;
  };

  const handleMerge = async () => {
    setMerging(true);
    try {
      const mergedData: any = {};
      for (const field of mergeableFields) {
        const choice = selectedFields[field.key as string] || 'primary';
        mergedData[field.key] = (choice === 'primary' ? primary : secondary)[field.key] as any;
      }
      await onMerge(primary.id, secondary.id, mergedData);
      onClose();
    } catch (error) {
      console.error('Merge failed:', error);
    } finally {
      setMerging(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={tr.dup.mergeTitle} size="lg">
      <div className="p-6 space-y-6">
        {/* Warning */}
        <div className="flex items-start p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-2 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-400">{tr.dup.mergeWarning}</p>
        </div>

        {/* Primary Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {tr.dup.selectPrimary}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {match.customers.map((c) => (
              <button
                key={c.id}
                onClick={() => setPrimaryId(c.id)}
                className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all text-left ${
                  primaryId === c.id
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
              >
                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                  primaryId === c.id ? 'border-green-500 bg-green-500' : 'border-gray-300'
                }`}>
                  {primaryId === c.id && <div className="w-1.5 h-1.5 bg-white rounded-full m-auto mt-0.5" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{getCustomerName(c)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.email || '—'}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Field-by-field merge */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {tr.dup.fieldByField}
          </label>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {mergeableFields.map((field) => {
              const primaryVal = (primary as any)[field.key];
              const secondaryVal = (secondary as any)[field.key];
              if (!primaryVal && !secondaryVal) return null;

              return (
                <div key={field.key as string} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
                    {field.label}
                  </span>
                  <div className="flex items-center space-x-2 flex-1">
                    <button
                      onClick={() => setSelectedFields(prev => ({ ...prev, [field.key as string]: 'primary' }))}
                      className={`flex-1 text-left px-2 py-1.5 rounded text-xs transition-all ${
                        selectedFields[field.key as string] !== 'secondary'
                          ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-medium'
                          : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {primaryVal || '—'}
                    </button>
                    <button
                      onClick={() => setSelectedFields(prev => ({ ...prev, [field.key as string]: 'secondary' }))}
                      className={`flex-1 text-left px-2 py-1.5 rounded text-xs transition-all ${
                        selectedFields[field.key as string] === 'secondary'
                          ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                          : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {secondaryVal || '—'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {tr.dup.cancel}
          </button>
          <button
            onClick={handleMerge}
            disabled={merging}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50"
          >
            {merging ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : null}
            {tr.dup.confirmMerge}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DuplicateMergeModal;
